/**
 * Auth Service
 * Handles all authentication operations and session management
 * for the URL shortener app.
 */

// Auth function endpoint
const AUTH_ENDPOINT = "/.netlify/functions/auth";

// Session storage keys
const SESSION_KEY = "richs_auth_session";

// ============================================================
// API CALLS
// ============================================================

/**
 * Call the auth serverless function
 * @param {Object} body - Request body with action and data
 * @returns {Promise<Object>} - Response data
 */
const callAuthApi = async (body) => {
  try {
    const response = await fetch(AUTH_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || "An error occurred");
    }

    return data;
  } catch (error) {
    console.error("Auth API error:", error);
    throw error;
  }
};

/**
 * Login with username and password
 * @param {string} username
 * @param {string} password
 * @param {boolean} rememberMe - If true, persist session across browser close
 * @returns {Promise<Object>} - User data on success
 */
export const login = async (username, password, rememberMe = false) => {
  const data = await callAuthApi({
    action: "login",
    username,
    password,
  });

  if (data.success && data.user) {
    // Store session
    saveSession(data.user, rememberMe);
    return data.user;
  }

  throw new Error("Login failed");
};

/**
 * Set password for first-time users
 * @param {string} username
 * @param {string} newPassword
 * @param {string} confirmPassword
 * @returns {Promise<Object>} - Success message
 */
export const setPassword = async (username, newPassword, confirmPassword) => {
  const data = await callAuthApi({
    action: "set-password",
    username,
    newPassword,
    confirmPassword,
  });

  return data;
};

/**
 * Change password for logged-in users
 * @param {string} username
 * @param {string} currentPassword
 * @param {string} newPassword
 * @param {string} confirmPassword
 * @returns {Promise<Object>} - Success message
 */
export const changePassword = async (username, currentPassword, newPassword, confirmPassword) => {
  const data = await callAuthApi({
    action: "change-password",
    username,
    currentPassword,
    newPassword,
    confirmPassword,
  });

  return data;
};

/**
 * Reset password using admin-provided reset code
 * @param {string} username
 * @param {string} resetCode
 * @param {string} newPassword
 * @param {string} confirmPassword
 * @returns {Promise<Object>} - Success message
 */
export const resetPassword = async (username, resetCode, newPassword, confirmPassword) => {
  const data = await callAuthApi({
    action: "reset-password",
    username,
    resetCode,
    newPassword,
    confirmPassword,
  });

  return data;
};

/**
 * Fetch all active users (for PIC dropdown)
 * @returns {Promise<Array>} - Array of user objects with username and displayName
 */
export const getActiveUsers = async () => {
  try {
    const response = await fetch(`${AUTH_ENDPOINT}?action=get-users`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });

    const data = await response.json();

    if (data.success && data.users) {
      return data.users;
    }

    return [];
  } catch (error) {
    console.error("Error fetching users:", error);
    return [];
  }
};

// ============================================================
// SESSION MANAGEMENT
// ============================================================

/**
 * Save session data to storage
 * @param {Object} user - User data (username, displayName, role)
 * @param {boolean} rememberMe - If true, use localStorage (persists); if false, use sessionStorage (clears on browser close)
 */
const saveSession = (user, rememberMe) => {
  const sessionData = {
    username: user.username,
    displayName: user.displayName,
    role: user.role,
    loginTime: new Date().toISOString(),
    rememberMe,
  };

  const storageData = JSON.stringify(sessionData);

  if (rememberMe) {
    // Persist across browser sessions
    localStorage.setItem(SESSION_KEY, storageData);
    // Clean up sessionStorage in case it has old data
    sessionStorage.removeItem(SESSION_KEY);
  } else {
    // Only persist for this browser session
    sessionStorage.setItem(SESSION_KEY, storageData);
    // Clean up localStorage in case it has old data
    localStorage.removeItem(SESSION_KEY);
  }
};

/**
 * Get current session data
 * @returns {Object|null} - Session data or null if not logged in
 */
export const getSession = () => {
  try {
    // Check localStorage first (remember me), then sessionStorage
    const stored =
      localStorage.getItem(SESSION_KEY) ||
      sessionStorage.getItem(SESSION_KEY);

    if (!stored) {
      return null;
    }

    const sessionData = JSON.parse(stored);

    // Basic validation - make sure required fields exist
    if (!sessionData.username || !sessionData.displayName) {
      clearSession();
      return null;
    }

    return sessionData;
  } catch (error) {
    console.error("Error reading session:", error);
    clearSession();
    return null;
  }
};

/**
 * Check if user is currently logged in
 * @returns {boolean}
 */
export const isLoggedIn = () => {
  return getSession() !== null;
};

/**
 * Get the current logged-in user's info
 * @returns {Object|null} - User object or null
 */
export const getCurrentUser = () => {
  const session = getSession();
  if (!session) return null;

  return {
    username: session.username,
    displayName: session.displayName,
    role: session.role,
  };
};

/**
 * Logout - clear all session data
 */
export const logout = () => {
  clearSession();
};

/**
 * Clear session from both storage types
 */
const clearSession = () => {
  localStorage.removeItem(SESSION_KEY);
  sessionStorage.removeItem(SESSION_KEY);
};

export default {
  login,
  setPassword,
  changePassword,
  resetPassword,
  getActiveUsers,
  getSession,
  isLoggedIn,
  getCurrentUser,
  logout,
};
